"use client";

import { useGate } from "statsig-react";
import { usePathname } from "next/navigation";

/**
 * Build a link from any page on the same venue to `/shows/{groupSlug}`,
 * respecting the mount point the page is rendered at:
 *  - apps/pages (`{slug}.punchup.live/*`): `/shows/{groupSlug}`
 *  - apps/web, `/venue/{slug}/*`:           `/venue/{slug}/shows/{groupSlug}`
 *  - apps/web, `/v/{slug}/*`:               `/v/{slug}/shows/{groupSlug}`
 *
 * We derive the base from `usePathname()` because a single shared UI
 * component (like `TourDateRow`) renders under all three mounts.
 *
 * Returns an empty string if either slug is empty, so callers can use the
 * result directly as `href={...}` and skip rendering when falsy.
 */
/**
 * Pure path builder shared by the gated and ungated hooks below. Resolves the
 * correct mount-point prefix from the current pathname. Returns "" if either
 * slug is empty so callers can use the result directly as `href`.
 */
export function buildVenueShowGroupPath(
  pathname: string,
  venueSlug: string | null | undefined,
  groupSlug: string | null | undefined
): string {
  if (!venueSlug || !groupSlug) return "";

  if (
    pathname === `/v/${venueSlug}` ||
    pathname.startsWith(`/v/${venueSlug}/`)
  ) {
    return `/v/${venueSlug}/shows/${groupSlug}`;
  }
  if (
    pathname === `/venue/${venueSlug}` ||
    pathname.startsWith(`/venue/${venueSlug}/`)
  ) {
    return `/venue/${venueSlug}/shows/${groupSlug}`;
  }
  // apps/pages — middleware rewrites the slug-prefixed path internally but
  // `usePathname()` returns the browser URL, so a root-relative link is fine.
  return `/shows/${groupSlug}`;
}

export function useVenueShowGroupHref(
  venueSlug: string | null | undefined,
  groupSlug: string | null | undefined
): string {
  // Hooks must always be called; bail out after reading them.
  const pathname = usePathname() ?? "";
  const { value: consumerPagesEnabled } = useGate("show_group_consumer_pages");
  if (!consumerPagesEnabled) return "";
  return buildVenueShowGroupPath(pathname, venueSlug, groupSlug);
}

/**
 * Ungated variant for the grouped-grid mode. Turning a venue's grid to
 * `grouped` is itself the explicit opt-in to surface group pages, and the
 * destination `/shows/{slug}` pages are not behind `show_group_consumer_pages`
 * — only the per-card links on the *individual* grid are. So a group card
 * always links to its drill-down.
 */
export function useVenueShowGroupPath(
  venueSlug: string | null | undefined,
  groupSlug: string | null | undefined
): string {
  const pathname = usePathname() ?? "";
  return buildVenueShowGroupPath(pathname, venueSlug, groupSlug);
}
